<template>
  <!-- Figma D3 "Selected Parts": collapsible grey bar with a count badge; each part edits qty / price / warranty inline
       (same rules as the old edit modal: qty 1–10, warranty locks qty 1 and price 0, max markup message). -->
  <div v-if="item && item.parts && item.parts.length" class="mt-[12px] rounded-[8px] bg-[#F1F1F1] font-roboto">
    <button type="button"
      class="w-full flex items-center justify-between gap-2 h-[40px] px-[12px] bg-transparent border-0"
      @click="open = !open">
      <span class="flex items-center gap-[8px] text-[14px] font-semibold text-[#333333]">
        Selected Parts
        <span
          class="min-w-[24px] h-[18px] px-[6px] rounded-full bg-[#7695FF] text-white text-[12px] leading-[18px] text-center">{{
            item.parts.length }}</span>
      </span>
      <i class="fa fa-angle-down !text-[24px] transition-transform text-[333333]" :class="open ? 'rotate-180' : ''"></i>
      <!-- <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="transition-transform"
        :class="open ? 'rotate-180' : ''" aria-hidden="true">
        <path d="M6 9l6 6 6-6" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg> -->
    </button>
    <div v-show="open" class="px-[8px] pb-[8px]">
      <ul
        class="list-none m-0 mobile:p-[0px] laptop:p-[12px] rounded-[8px] mobile:border-[0px] laptop:border-1 border-[#E9ECF1] mobile:bg-transparent laptop:bg-white mobile:flex flex-col gap-[10px] laptop:block">
        <li v-for="(opt, index) in item.parts" :key="index"
          class="py-[12px] laptop:first:pt-0 laptop:last:pb-0 border-b-[1px] last:border-b-0 border-[#EDEFF3] bg-[#fff] px-[15px] laptop:px-0 rounded-[8px]">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="m-0 text-[13px] laptop:text-[14px] text-[#333333] break-words">{{ opt.part_name }}</p>
              <p v-if="opt.category_name" class="m-0 mt-[2px] text-[12px] text-[#A8AEB8]">{{ opt.category_name }}</p>
            </div>

            <button type="button" :class="bookingResponse.is_flat_price_model ? 'flex' : 'mobile:hidden laptop:flex'"
              class="p-0 leading-none w-[30px] h-[30px] rounded-full transition-all duration-300 flex items-center justify-center border !border-[#EFEFEF] text-[#F67280] !bg-[#fff] hover:text-[#fff] hover:!bg-[#F67280]"
              aria-label="Delete note" @click="removeItem(opt)">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"
                class="w-[16px] h-[16px]">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="currentColor" fill-rule="evenodd"
                  d="M9.774 5L3.758 3.94l.174-.986a.5.5 0 0 1 .58-.405L18.411 5h.088h-.087l1.855.327a.5.5 0 0 1 .406.58l-.174.984l-2.09-.368l-.8 13.594A2 2 0 0 1 15.615 22H8.386a2 2 0 0 1-1.997-1.883L5.59 6.5h12.69zH5.5zM9 9l.5 9H11l-.4-9zm4.5 0l-.5 9h1.5l.5-9zm-2.646-7.871l3.94.694a.5.5 0 0 1 .405.58l-.174.984l-4.924-.868l.174-.985a.5.5 0 0 1 .58-.405z" />
              </svg>
            </button>
          </div>
          <!-- below laptop (Figma mobile D3): label left / control right rows, stepper in a pill, delete next to Part Warranty -->
          <div v-if="!bookingResponse.is_flat_price_model"
            class="flex mobile:flex-col mobile:items-stretch laptop:flex-row laptop:flex-wrap laptop:items-center justify-between gap-x-[20px] gap-y-[10px] mt-[10px]">
            <div
              class="flex items-center mobile:justify-between laptop:justify-start gap-[10px] text-[14px] text-[#666666]">
              Quantity:
              <span
                class="flex items-center gap-[4px] laptop:gap-[10px] mobile:p-[3px] mobile:rounded-full mobile:border-[1px] border-solid mobile:border-[#EFEFEF]">
                <button type="button" aria-label="Decrease quantity"
                  class="mobile:w-[24px] mobile:h-[24px] laptop:w-[20px] laptop:h-[20px] rounded-full border-0 bg-[#E9ECF1] text-[#333333] leading-none disabled:opacity-50"
                  :disabled="opt.quantity <= 1 || opt.is_warranty == 1" @click="opt.quantity--">−</button>
                <span class="mobile:min-w-[28px] laptop:min-w-[16px] text-center text-[15px] text-[#333333]">{{
                  opt.quantity }}</span>
                <button type="button" aria-label="Increase quantity"
                  class="mobile:w-[24px] mobile:h-[24px] laptop:w-[20px] laptop:h-[20px] rounded-full border-0 bg-[#7695FF] text-white leading-none disabled:opacity-50"
                  :disabled="opt.quantity >= 10 || opt.is_warranty == 1" @click="opt.quantity++">+</button>
              </span>
            </div>
            <div
              class="flex mobile:flex-col mobile:items-stretch laptop:flex-row laptop:flex-wrap laptop:items-center mobile:gap-[10px] laptop:gap-[15px]">
              <label
                class="flex items-center mobile:justify-between laptop:justify-start gap-[10px] m-0 text-[14px] text-[#666666]">
                {{ opt.is_special ? 'Special Part Price Per Unit:' : 'Part Price Per Unit:' }}
                <span
                  class="flex items-center h-[30px] mobile:rounded-full laptop:rounded-[5px] border-1 border-[#EFEFEF] overflow-hidden"
                  :class="opt.is_warranty == 1 ? 'bg-[#F5F5F7]' : 'bg-white'">
                  <span
                    class="w-[28px] h-full flex items-center justify-center bg-[#A3AAD2] text-white text-[16px] rounded-full laptop:rounded-[5px]">$</span>
                  <input v-model="opt.cost_with_markup" type="text" inputmode="decimal" size="6"
                    :disabled="opt.is_warranty == 1"
                    class="w-[60px] h-full px-[6px] border-0 outline-none bg-transparent text-[14px] text-[#333333]"
                    @paste.prevent @keypress="isNumber($event)" @input="isNumber($event)">
                </span>
              </label>
              <span class="flex items-center justify-between gap-[8px]">
                <label class="flex items-center gap-[8px] m-0 cursor-pointer select-none text-[14px] text-[#666666]">
                  <input v-model="opt.is_warranty" type="checkbox" class="sr-only" @change="warrantyChanged(opt)">
                  <span class="w-[20px] h-[20px] rounded-[3px] border-1 bg-white flex items-center justify-center"
                    :class="opt.is_warranty ? 'border-[#7695FF]' : 'border-[#CCCCCC]'">
                    <svg v-if="opt.is_warranty" width="13" height="13" viewBox="0 0 14 14" fill="none"
                      aria-hidden="true">
                      <path d="M3 7.4l2.6 2.5L11 4.4" stroke="#7695FF" stroke-width="1.6" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </span>
                  Part Warranty
                </label>
                <button type="button"
                  class="laptop:hidden p-0 leading-none w-[30px] h-[30px] rounded-full flex items-center justify-center text-[#fff] !bg-[#F67280]"
                  aria-label="Delete note" @click="removeItem(opt)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"
                    class="w-[16px] h-[16px]">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" fill-rule="evenodd"
                      d="M9.774 5L3.758 3.94l.174-.986a.5.5 0 0 1 .58-.405L18.411 5h.088h-.087l1.855.327a.5.5 0 0 1 .406.58l-.174.984l-2.09-.368l-.8 13.594A2 2 0 0 1 15.615 22H8.386a2 2 0 0 1-1.997-1.883L5.59 6.5h12.69zH5.5zM9 9l.5 9H11l-.4-9zm4.5 0l-.5 9h1.5l.5-9zm-2.646-7.871l3.94.694a.5.5 0 0 1 .405.58l-.174.984l-4.924-.868l.174-.985a.5.5 0 0 1 .58-.405z" />
                  </svg>
                </button>
              </span>
            </div>
          </div>
          <p v-if="opt.is_max_markup_applicable && Number(opt.cost_with_markup) > Number(opt.max_allowed_price)"
            class="m-0 mt-[6px] text-right text-[11px] text-[#EE5851]">
            Price cannot exceed ${{ opt.max_allowed_price }}.
          </p>
        </li>
      </ul>
    </div>

    <!-- Modal -->
    <div id="singleSelectedPart" ref="singleSelectedPart" class="modal fade" tabindex="-1"
      aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-md">
        <div class="modal-content">
          <div class="modal-body pt-2 px-0 pb-4">
            <!-- <div class="closebutton d-flex justify-content-end">
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                @click="hideModal"
              ></button>
            </div> -->
            <div class="modalCntnt">
              <nest_modal_part_card :item="selectedPart" :index="selectedPartIndex" :selected-item="item" />
              <div class="bottomButtons d-flex justify-content-center mt-3">
                <button class="modalBtn buttonPrimary d-flex align-items-center text-uppercase me-2"
                  data-bs-dismiss="modal" @click="hideModal">
                  Cancel
                </button>
                <button class="modalBtn buttonSecondary d-flex align-items-center text-uppercase" @click="saveChanges">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters } from "vuex";

import nest_modal_part_card from "../../components/nest_components/nest_modal_parts_card.vue";
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
const toast = useToast();
export default {
  components: {
    nest_modal_part_card,
  },
  props: ["item"],
  computed: {
    ...mapGetters([
      "bookingResponse"
    ]),

  },
  data() {
    return {
      modal: null,
      selectedPart: null,
      selectedPartIndex: 0,
      open: true,
    };
  },

  mounted() {
    const modalElement = document.getElementById("singleSelectedPart");
    // the modal only renders when the item has parts
    if (!modalElement) return;
    modalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
    modalElement.addEventListener("hidden.bs.modal", this.removeOverflowHidden);
  },

  beforeUnmount() {
    const modalElement = document.getElementById("singleSelectedPart");
    if (!modalElement) return;
    modalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
    modalElement.removeEventListener(
      "hidden.bs.modal",
      this.removeOverflowHidden
    );
  },

  methods: {
    addOverflowHidden() {
      document.documentElement.classList.add("overflow-hidden");
    },

    removeOverflowHidden() {
      document.documentElement.classList.remove("overflow-hidden");
    },
    // warranty part: qty 1, price 0 (old modal rule)
    warrantyChanged(data) {
      if (data.is_warranty) {
        data.cost_with_markup = "0";
        data.quantity = "1";
      }
    },

    // price: digits + one dot, max 4 integer / 2 decimal digits (old modal rule)
    isNumber(evt) {
      const inputElement = evt.target;
      let value = inputElement.value;
      if (evt.type === 'input') {
        value = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        const [integer, decimal] = value.split('.');
        if (integer.length > 4) value = integer.slice(0, 4);
        if (decimal) value = integer === "" ? "" : `${integer}.${decimal.slice(0, 2)}`;
        if (value.startsWith('.')) value = '';
        if (inputElement.value !== value) {
          inputElement.value = value;
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else if (evt.type === 'keypress') {
        const charCode = evt.which || evt.keyCode;
        if (charCode === 46 && value.indexOf('.') === -1) return true;
        if (charCode >= 48 && charCode <= 57) return true;
        evt.preventDefault();
        return false;
      }
    },

    removeItem(data) {
      var i = this.item;
      i.parts = i.parts.filter(function (item) {
        return item !== data;
      });
    },

    editItem(data, indx) {
      this.selectedPart = JSON.parse(JSON.stringify(data));
      this.selectedPartIndex = indx;
      this.modal = new Modal(this.$refs.singleSelectedPart);
      this.modal.show();
    },

    saveChanges() {
      // Add your save logic here
      console.log(this.selectedPart);

      let res = true;
      let msg = "";
      if (!this.selectedPart["is_warranty"] && this.selectedPart.cost_with_markup.length <= 0) {
        msg = "Price field is required.";
        res = false;
      }

      if (this.selectedPart.is_max_markup_applicable &&
        this.selectedPart.cost_with_markup > this.selectedPart.max_allowed_price) {
        msg = "Please fix price errors before saving";
        res = false;
      }


      if (res) {
        this.item.parts[this.selectedPartIndex] = this.selectedPart;
        this.hideModal();
      } else {
        toast.error(msg);
      }


    },

    hideModal() {
      if (this.modal) {
        this.modal.hide();
        this.removeBackdrop();
      }
    },

    removeBackdrop() {
      document.querySelectorAll(".modal-backdrop").forEach((backdrop) => {
        backdrop.remove();
      });
    },
  },
};
</script>

<style>
div#singleSelectedPart .partsCard {
  box-shadow: none;
  margin: 0 0.5rem !important;
}
</style>
